import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PageLayout from "../../../components/PageLayout";
import NavBar from "../../../NavBar/NavBar";
import { style, classes } from "typestyle";
import Jumbotron from "../../../components/Jumbotron";
import Throbber from "../../../components/Throbber";
import MatrixCalculatorTable from "./MatrixCalculatorTable";
import MatrixCalculatorCsv from "./MatrixCalculatorCsv";
import Button from "../../../components/Button";
import { hSpaced, vSpaced } from "../../../../cssUtils";
import { ensureCurrentSite } from "../../../../redux/modules/currentSite/asyncActions";
import { getCurrentSiteState } from "../../../../redux/modules/rootReducer";
import type { CurrentSiteState } from "../../../../redux/modules/currentSite/reducer";
import { ISRS } from "../types";

type MatrixCalculatorPageProps = {
  customerId: string,
  siteId: string,
  dispatch: Function,
  currentSiteState: CurrentSiteState,
};

const MatrixCalculatorPage = ({
  customerId,
  siteId,
  dispatch,
  currentSiteState,
}: MatrixCalculatorPageProps) => {
  const [srs, setSrs] = useState<ISRS | null>(null);
  const [pending] = useState(!currentSiteState);
  !srs &&
    currentSiteState.data &&
    currentSiteState.data.srs &&
    setSrs(currentSiteState.data.srs);

  // This is React Hook for side effects (https://reactjs.org/docs/hooks-effect.html)
  useEffect(() => {
    // TODO: replace request with dispatch(ensureSite())
    dispatch(ensureCurrentSite(customerId, siteId));
  }, [customerId, siteId]);

  // TODO: don't forget to handle site error
  return (
    <PageLayout
      navBar={<NavBar />}
      header={
        <Jumbotron
          imageUrl={"logo-dashboard-matrix-calculator.svg"}
          header={"Matrix Calculator"}
          subheader={null}
        />
      }
      content={<Content srs={srs} pending={pending} />}
    />
  );
};

interface IContentProps {
  srs: ISRS | null;
  pending: boolean;
}

const Content = ({ srs, pending }: IContentProps) => {
  const [tabType, setTabType] = useState("table");

  // Or maybe current site error can be displayed here..

  if (pending) {
    return (
      <div className={style({ display: "flex", justifyContent: "center" })}>
        <Throbber />
      </div>
    );
  }

  if (!srs) {
    return (
      <div className={style({ display: "flex", justifyContent: "center" })}>
        No SRS found. Matrix Calculator can&apos;t work without SRS. Try to
        complete onboarding process.
      </div>
    );
  }

  if (tabType === "table") {
    return (
      <div className={style(vSpaced(24))}>
        <ShiftedCsWarning />
        <TabButtons
          activeTabType={tabType}
          onTabClick={(newTabType: TabType) => setTabType(newTabType)}
        />
        <MatrixCalculatorTable srs={srs} />
      </div>
    );
  }

  if (tabType === "csv") {
    return (
      <div className={style(vSpaced(24))}>
        <ShiftedCsWarning />
        <TabButtons
          activeTabType={tabType}
          onTabClick={(newTabType: TabType) => setTabType(newTabType)}
        />
        <MatrixCalculatorCsv srs={srs} />
      </div>
    );
  }

  throw new Error(`Unknown tab type ${tabType}`);
};

type TabType = "table" | "csv";
type TabButtonsProps = {
  activeTabType: TabType,
  onTabClick: (tabId: TabType) => void,
};
const TabButtons = ({ activeTabType, onTabClick }: TabButtonsProps) => (
  <>
    <div className={style(hSpaced(16))}>
      <Button
        type={activeTabType === "table" ? "primary" : "secondary"}
        size="big"
        label="Manual Input"
        onClick={() => onTabClick("table")}
      />
      <Button
        type={activeTabType === "csv" ? "primary" : "secondary"}
        size="big"
        label="CSV Converter"
        onClick={() => onTabClick("csv")}
      />
    </div>
  </>
);

const ShiftedCsWarning = () => (
  <div className={classes(style(vSpaced(8)), style({ padding: "0 24px" }))}>
    <div
      className={style({
        fontFamily: "Roboto",
        fontStyle: "normal",
        fontWeight: 500,
        fontSize: "14px",
        lineHeight: "16px",
        /* identical to box height, or 114% */
        color: "rgba(0, 0, 0, 0.87)",
      })}
    >
      Beware!
    </div>
    <div
      className={style({
        fontFamily: "Roboto",
        fontStyle: "normal",
        fontWeight: "normal",
        fontSize: "14px",
        lineHeight: "20px",
      })}
    >
      This is the converter between the systems of coordinates used for the
      processing of the scans. They may be: shifted WSG84, NAD or UTM (Global),
      and site-specific assumed systems of coordinates.
    </div>
    <div
      className={style({
        fontFamily: "Roboto",
        fontStyle: "normal",
        fontWeight: "normal",
        fontSize: "14px",
        lineHeight: "16px",
      })}
    >
      Coordinates in WGS84 and Global ones may be shifted from a real position.
    </div>
  </div>
);

export default connect(state => ({
  currentSiteState: getCurrentSiteState(state),
}))(MatrixCalculatorPage);
